/**
 * ss-modal.js
 * Quick-view modal + AJAX add-to-cart → Horizon cart drawer integration.
 *
 * Cart drawer open mechanism:
 *   After a successful add-to-cart we:
 *   1. POST /cart/add.js with sections:'cart-drawer-section' so Shopify returns
 *      fresh rendered HTML for the drawer in the same response.
 *   2. Replace the live #shopify-section-cart-drawer-section innerHTML with the
 *      freshly-rendered HTML so the drawer shows the new item.
 *   3. Open the drawer via #cart-drawer .open() (ThemeDrawer custom element).
 *
 * Section Rendering API URL for quick-view:
 *   {product_url}?section_id=ss-quickview
 *   e.g. /products/sure-shot-stick-pack?section_id=ss-quickview
 */

(function () {
  'use strict';

  /* ————————————————————————————
     1.  MODAL SHELL (singleton)
  ———————————————————————————— */

  let dialog = null;
  let isOpen = false;

  function buildDialog() {
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'ss-modal-dialog';
    dialog.setAttribute('aria-label', 'Product quick view');
    dialog.setAttribute('aria-modal', 'true');

    const closeBtn = document.createElement('button');
    closeBtn.className = 'ss-modal__close';
    closeBtn.setAttribute('type', 'button');
    closeBtn.setAttribute('aria-label', 'Close quick view');
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', closeModal);

    const inner = document.createElement('div');
    inner.className = 'ss-modal__inner';
    inner.id = 'ss-modal-content';

    dialog.appendChild(closeBtn);
    dialog.appendChild(inner);
    document.body.appendChild(dialog);

    // Click outside (backdrop) → close
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) closeModal();
    });

    // ESC handled natively by <dialog>; also sync our state
    dialog.addEventListener('close', function () {
      isOpen = false;
      document.body.style.overflow = '';
    });

    return dialog;
  }

  function openModal() {
    const d = buildDialog();
    if (!d.open) {
      d.showModal();
    }
    isOpen = true;
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    if (dialog && dialog.open) {
      dialog.close();
    }
    isOpen = false;
    document.body.style.overflow = '';
  }

  /* ————————————————————————————
     2.  SECTION RENDERING FETCH
  ———————————————————————————— */

  async function loadQuickView(productUrl) {
    const d = buildDialog();
    // Remove content-modal modifier in case dialog was previously used for contact/legal
    d.classList.remove('ss-modal-dialog--content');
    d.setAttribute('aria-label', 'Product quick view');
    const inner = d.querySelector('#ss-modal-content');

    // Show loading state
    inner.innerHTML = `
      <div class="ss-modal__loading" aria-live="polite" aria-busy="true">
        <span class="ss-modal__spinner" aria-hidden="true"></span>
        Loading…
      </div>`;
    openModal();

    // Build Section Rendering API URL
    // Normalise: strip query/hash, then add section param
    let url;
    try {
      const base = productUrl.split('?')[0].split('#')[0];
      url = base + '?section_id=ss-quickview';
    } catch (_) {
      url = productUrl + '?section_id=ss-quickview';
    }

    try {
      const resp = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!resp.ok) throw new Error('fetch-failed');

      const html = await resp.text();
      inner.innerHTML = html;
      inner.setAttribute('aria-busy', 'false');

      // Wire up gallery thumbnails & selling-plan radio selectors
      // (mirrors ss-buybox inline script logic)
      initQuickViewInteractions(inner, productUrl);

      // Wire AJAX add-to-cart
      const form = inner.querySelector('.ss-qv__form');
      if (form) wireAjaxAtc(form);

    } catch (err) {
      console.warn('[ss-modal] Section fetch failed:', err);
      inner.innerHTML = `
        <div class="ss-modal__error" role="alert">
          <p>Couldn't load product details.</p>
          <a href="${productUrl}">View full product page →</a>
        </div>`;
    }
  }

  /* ————————————————————————————
     3.  QUICK-VIEW INTERACTIONS
  ———————————————————————————— */

  function initQuickViewInteractions(container, productUrl) {
    // Thumbnail gallery switch
    const mainImg = container.querySelector('.ss-qv__main-img');
    container.querySelectorAll('.ss-qv__thumb').forEach(function (btn) {
      btn.addEventListener('click', function () {
        container.querySelectorAll('.ss-qv__thumb').forEach(function (b) {
          b.classList.remove('ss-qv__thumb--active');
        });
        btn.classList.add('ss-qv__thumb--active');
        const full = btn.getAttribute('data-full');
        if (full && mainImg) {
          mainImg.src = full;
          mainImg.srcset = '';
        }
      });
    });

    // Selling-plan radio highlight
    container.querySelectorAll('.ss-qv__opt-radio').forEach(function (radio) {
      radio.addEventListener('change', function () {
        container.querySelectorAll('.ss-qv__opt').forEach(function (opt) {
          opt.classList.remove('ss-qv__opt--sel');
        });
        const opt = this.closest('.ss-qv__opt');
        if (opt) opt.classList.add('ss-qv__opt--sel');
      });
    });
  }

  /* ————————————————————————————
     4.  AJAX ADD-TO-CART
     Steps:
       a) Read variant id and optional selling_plan from the form.
       b) POST /cart/add.js as JSON, including sections:'cart-drawer-section'
          so Shopify returns fresh drawer HTML in the same response.
       c) Parse response.sections['cart-drawer-section'] and replace the live
          #shopify-section-cart-drawer-section innerHTML so the drawer is up-to-date.
       d) Open the drawer via #cart-drawer .open() (ThemeDrawer custom element).
  ———————————————————————————— */

  var CART_DRAWER_SECTION_ID = 'cart-drawer-section';

  function wireAjaxAtc(form) {
    form.addEventListener('submit', async function (e) {
      e.preventDefault();

      const btn = form.querySelector('.ss-qv__atc-btn');
      const originalText = btn ? btn.textContent : 'Add to cart';

      // Loading state
      if (btn) {
        btn.setAttribute('data-loading', 'true');
        btn.setAttribute('disabled', 'true');
        btn.textContent = 'Adding';
      }

      try {
        // Read variant id from the hidden id input
        const variantInput = form.querySelector('input[name="id"]');
        const variantId = variantInput ? parseInt(variantInput.value, 10) : null;

        if (!variantId) throw new Error('no-variant-id');

        // Read optional selling_plan from the checked radio
        const sellingPlanInput = form.querySelector('input[name="selling_plan"]:checked')
          || form.querySelector('input[name="selling_plan"]');
        const sellingPlan = sellingPlanInput && sellingPlanInput.value
          ? parseInt(sellingPlanInput.value, 10)
          : undefined;

        // Build the JSON payload including sections so Shopify returns fresh
        // drawer HTML without a second round-trip.
        const payload = {
          items: [{
            id: variantId,
            quantity: 1
          }],
          sections: CART_DRAWER_SECTION_ID,
          sections_url: window.location.pathname
        };

        if (sellingPlan) {
          payload.items[0].selling_plan = sellingPlan;
        }

        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });

        if (!resp.ok) {
          const errData = await resp.json().catch(function () { return {}; });
          throw new Error(errData.description || 'add-failed');
        }

        const data = await resp.json();

        // Refresh the cart drawer contents before opening it.
        // data.sections[CART_DRAWER_SECTION_ID] contains the freshly rendered
        // section HTML when the sections param is sent with the add request.
        var sectionHtml = data.sections && data.sections[CART_DRAWER_SECTION_ID];
        if (sectionHtml) {
          replaceCartDrawerContents(sectionHtml);
        } else {
          // Fallback: fetch the section separately if Shopify didn't return it
          // (e.g. the cart type is not 'drawer' or the feature flag is off).
          await refreshCartDrawerViaSectionAPI();
        }

        // Close modal first so focus restoration runs before the drawer opens
        closeModal();

        // Open Horizon's cart drawer
        openCartDrawer();

      } catch (err) {
        console.warn('[ss-modal] ATC failed:', err);
        if (btn) {
          btn.removeAttribute('data-loading');
          btn.removeAttribute('disabled');
          btn.textContent = originalText;
        }
        // Surface error briefly
        const errEl = document.createElement('p');
        errEl.style.cssText = 'color:#d63250;font-size:13px;font-weight:700;margin-top:8px;text-align:center;';
        errEl.textContent = 'Could not add to cart — please try again.';
        form.appendChild(errEl);
        setTimeout(function () { errEl.remove(); }, 3500);
      }
    });
  }

  /* ————————————————————————————
     5.  CART DRAWER REFRESH + OPEN

     replaceCartDrawerContents(html):
       Parses the section HTML returned by /cart/add.js?sections= and replaces
       the live #shopify-section-cart-drawer-section innerHTML so the drawer
       shows the newly added item when it opens.
       Container: #shopify-section-cart-drawer-section (the Shopify section wrapper)

     openCartDrawer():
       Opens the drawer via ThemeDrawer.open().
       Falls back to a full section fetch then /cart if needed.
  ———————————————————————————— */

  function replaceCartDrawerContents(sectionHtml) {
    try {
      var parser = new DOMParser();
      var doc = parser.parseFromString(sectionHtml, 'text/html');

      var sectionWrapperId = 'shopify-section-' + CART_DRAWER_SECTION_ID;
      var newSection = doc.getElementById(sectionWrapperId);
      var existingSection = document.getElementById(sectionWrapperId);

      if (existingSection && newSection) {
        // Replace the entire innerHTML so custom elements re-initialise with
        // fresh cart state. The <script type="module"> inside the section is
        // idempotent (guarded by customElements.get) so re-insertion is safe.
        existingSection.innerHTML = newSection.innerHTML;
      } else if (existingSection && !newSection) {
        // The section wrapper is missing in the response (render_wrapper:false path).
        // Fall back to replacing with the raw response HTML.
        existingSection.innerHTML = sectionHtml;
      }
    } catch (err) {
      console.warn('[ss-modal] replaceCartDrawerContents failed:', err);
    }
  }

  async function refreshCartDrawerViaSectionAPI() {
    try {
      var resp = await fetch('/?section_id=' + CART_DRAWER_SECTION_ID, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!resp.ok) throw new Error('section-fetch-failed');
      var html = await resp.text();
      replaceCartDrawerContents(html);
    } catch (err) {
      console.warn('[ss-modal] refreshCartDrawerViaSectionAPI failed:', err);
    }
  }

  function openCartDrawer() {
    // Primary: ThemeDrawer.open() on the #cart-drawer element
    var cartDrawerEl = document.querySelector('#cart-drawer');
    if (cartDrawerEl && typeof cartDrawerEl.open === 'function') {
      cartDrawerEl.open();
      return;
    }

    // Fallback: navigate to /cart
    window.location.href = '/cart';
  }

  /**
   * Close the cart drawer if it is open (ThemeDrawer custom element).
   * Used when an in-drawer link is redirected to an on-page action so the
   * drawer does not stay open over the destination.
   */
  function closeCartDrawer() {
    var cartDrawerEl = document.querySelector('#cart-drawer');
    if (cartDrawerEl && typeof cartDrawerEl.close === 'function') {
      cartDrawerEl.close();
    }
  }

  /* ————————————————————————————
     6.  EVENT DELEGATION
     Listen for clicks on [data-ss-quickview] anywhere in the page.
     Value = product URL or /products/handle path.
  ———————————————————————————— */

  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-ss-quickview]');
    if (!trigger) return;

    e.preventDefault();
    const productUrl = trigger.getAttribute('data-ss-quickview');
    if (!productUrl) return;

    // Respect prefers-reduced-motion — fall back to navigation
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.location.href = productUrl;
      return;
    }

    loadQuickView(productUrl);
  });

  /* ————————————————————————————
     7.  GENERIC CONTENT MODAL
     Loads any page section into the modal via the Section Rendering API.

     Trigger attributes:
       data-ss-modal      = page URL to fetch (e.g. /pages/contact)
       data-ss-section    = section_id to append (e.g. ss-contact, ss-legal)

     On fetch failure the handler falls back to full navigation so the link
     always works.

     Also intercepts document-wide link clicks whose href matches:
       • /pages/contact            → Section Rendering API (ss-contact section)
       • /policies/*               → fetch full HTML + DOMParser content extraction
       • /pages/(privacy|terms|refund-policy|shipping|accessibility)
                                   → same fetch+extract path as /policies/
  ———————————————————————————— */

  /**
   * Ordered list of CSS selectors used to extract policy body content
   * from a full Shopify policy page response.  The first selector that
   * returns a non-empty element wins.
   */
  var POLICY_CONTENT_SELECTORS = [
    'main .shopify-policy__container',
    '.shopify-policy__body',
    '.shopify-policy__container',
    'main',
    '[role="main"]'
  ];

  /**
   * Patterns that identify policy URLs (fetch+extract path).
   * Includes both /policies/ routes and /pages/ aliases.
   */
  var POLICY_PATTERNS = [
    /^\/policies\//i,
    /^\/pages\/privacy(\/)?$/i,
    /^\/pages\/terms(\/)?$/i,
    /^\/pages\/refund-policy(\/)?$/i,
    /^\/pages\/shipping(\/)?$/i,
    /^\/pages\/accessibility(\/)?$/i
  ];

  /** Pattern that identifies the contact page (Section Rendering path). */
  var CONTACT_PATTERN = /^\/pages\/contact(\/)?$/i;

  /**
   * Return true if the click event has a modifier key pressed or the link
   * opens in a new tab — those should navigate normally.
   *
   * @param {MouseEvent} e
   * @param {HTMLAnchorElement} link
   * @returns {boolean}
   */
  function isModifiedClick(e, link) {
    return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey
      || link.target === '_blank'
      || link.getAttribute('download') != null;
  }

  /**
   * Set the modal into "content" mode (narrower, scrollable) and show the
   * loading spinner.  Returns { d, inner } ready to populate.
   *
   * @param {string} ariaLabel
   * @returns {{ d: HTMLDialogElement, inner: HTMLElement }}
   */
  function prepareContentModal(ariaLabel) {
    var d = buildDialog();
    d.classList.add('ss-modal-dialog--content');
    d.setAttribute('aria-label', ariaLabel || 'Page content');

    var inner = d.querySelector('#ss-modal-content');
    inner.innerHTML = [
      '<div class="ss-modal__loading" aria-live="polite" aria-busy="true">',
      '<span class="ss-modal__spinner" aria-hidden="true"></span>',
      'Loading…',
      '</div>'
    ].join('');

    openModal();
    return { d: d, inner: inner };
  }

  /**
   * Load the contact page via Section Rendering API (ss-contact section).
   * Falls back to full navigation on fetch failure.
   *
   * @param {string} pageUrl  - e.g. /pages/contact
   * @param {string} sectionId
   */
  async function loadContactModal(pageUrl, sectionId) {
    var ref = prepareContentModal('Contact');
    var inner = ref.inner;

    var base = pageUrl.split('?')[0].split('#')[0];
    var fetchUrl = base + '?section_id=' + sectionId;

    try {
      var resp = await fetch(fetchUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!resp.ok) throw new Error('fetch-failed');

      var html = await resp.text();
      inner.innerHTML = html;
      inner.setAttribute('aria-busy', 'false');

    } catch (err) {
      console.warn('[ss-modal] Contact section fetch failed:', err);
      closeModal();
      window.location.href = pageUrl;
    }
  }

  /**
   * Load a policy page by fetching its full HTML, parsing with DOMParser,
   * and extracting the policy body content.
   * Falls back to full navigation on fetch failure or empty content.
   *
   * @param {string} pageUrl  - e.g. /policies/refund-policy
   */
  async function loadPolicyModal(pageUrl) {
    var ref = prepareContentModal('Policy');
    var inner = ref.inner;

    var base = pageUrl.split('?')[0].split('#')[0];

    try {
      var resp = await fetch(base, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!resp.ok) throw new Error('fetch-failed');

      var html = await resp.text();
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');

      // Extract page title for the modal heading
      var titleEl = doc.querySelector('h1') || doc.querySelector('title');
      var title = titleEl ? titleEl.textContent.trim() : '';

      // Try selectors in order until we find non-empty content
      var contentEl = null;
      for (var si = 0; si < POLICY_CONTENT_SELECTORS.length; si++) {
        var candidate = doc.querySelector(POLICY_CONTENT_SELECTORS[si]);
        if (candidate && candidate.textContent.trim().length > 0) {
          contentEl = candidate;
          break;
        }
      }

      if (!contentEl) throw new Error('no-content');

      // Sanitise: remove any <script> or <style> tags from extracted content
      contentEl.querySelectorAll('script, style').forEach(function (el) {
        el.remove();
      });

      var heading = title
        ? '<h2 class="ss-legal__title">' + escapeHtml(title) + '</h2>'
        : '';

      inner.innerHTML = [
        '<div class="ss-legal__body rte">',
        heading,
        contentEl.innerHTML,
        '</div>'
      ].join('');

      inner.setAttribute('aria-busy', 'false');

    } catch (err) {
      console.warn('[ss-modal] Policy fetch/extract failed:', err);
      closeModal();
      window.location.href = pageUrl;
    }
  }

  /**
   * Minimal HTML escaper for trusted-source strings (page titles).
   * @param {string} str
   * @returns {string}
   */
  function escapeHtml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Fetch a page section via the Section Rendering API and show it in the
   * modal.  Falls back to navigation on any fetch error.
   * Kept for backwards-compatibility with explicit [data-ss-modal] triggers.
   *
   * @param {string} pageUrl   - The page URL (e.g. /pages/contact)
   * @param {string} sectionId - The section_id to render (e.g. ss-contact)
   */
  async function loadContentModal(pageUrl, sectionId) {
    await loadContactModal(pageUrl, sectionId);
  }

  // Handle explicit [data-ss-modal] triggers (data-ss-section optional)
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-ss-modal]');
    if (!trigger) return;

    var pageUrl = trigger.getAttribute('data-ss-modal');
    if (!pageUrl) return;

    e.preventDefault();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.location.href = pageUrl;
      return;
    }

    var sectionId = trigger.getAttribute('data-ss-section') || 'ss-contact';
    loadContentModal(pageUrl, sectionId);
  });

  // Document-wide interception of contact and policy/legal links.
  // Handles admin-managed Shopify menus where we cannot add data attributes,
  // including /policies/* routes that the Section Rendering API does not support.
  document.addEventListener('click', function (e) {
    // Skip if already handled by explicit data-ss-modal or data-ss-quickview
    if (e.target.closest('[data-ss-modal]')) return;
    if (e.target.closest('[data-ss-quickview]')) return;

    var link = e.target.closest('a[href]');
    if (!link) return;

    // Let modifier-key clicks and new-tab links navigate normally
    if (isModifiedClick(e, link)) return;

    var pathname = link.pathname || '';

    // ── Home page only: keep every shop link on-page ──────────────────────
    // The home template is identifiable by the #shop anchor. There we convert
    // internal product links to the quick-view modal and collection links to a
    // smooth scroll to the on-page shop grid, so nothing — including the
    // cart-drawer empty-state "Continue shopping" link and product
    // recommendations — navigates the user off the page.
    var sameOrigin = link.hostname === window.location.hostname;
    var shopAnchor = document.getElementById('shop');
    if (sameOrigin && shopAnchor) {
      // Internal product link → quick-view modal
      if (/^\/products\//i.test(pathname)) {
        e.preventDefault();
        closeCartDrawer();
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          window.location.href = link.href;
          return;
        }
        loadQuickView(pathname);
        return;
      }
      // Internal collection link → scroll to the on-page shop grid
      if (/^\/collections\//i.test(pathname)) {
        e.preventDefault();
        closeCartDrawer();
        shopAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }

    // ── Contact page → Section Rendering ──────────────────────────────────
    if (CONTACT_PATTERN.test(pathname)) {
      e.preventDefault();

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        window.location.href = link.href;
        return;
      }

      loadContactModal(pathname, 'ss-contact');
      return;
    }

    // ── Policy / legal pages → fetch full HTML + extract ──────────────────
    var isPolicyUrl = false;
    for (var pi = 0; pi < POLICY_PATTERNS.length; pi++) {
      if (POLICY_PATTERNS[pi].test(pathname)) {
        isPolicyUrl = true;
        break;
      }
    }

    if (!isPolicyUrl) return;

    e.preventDefault();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.location.href = link.href;
      return;
    }

    loadPolicyModal(pathname);
  });

})();
